import { ChevronDownMedium, ChevronUpMedium } from '@zoopla-eng/zds/icons';
import { Select } from '@zoopla-eng/zds/select';
import { fontGroups } from '@zoopla-eng/zds/themes';
import { buildSelectPriceTitleToRent } from '@zoopla/redux-reducer-filters';
import { MutableRefObject, useRef, useState } from 'react';
import { DropdownContainer } from '../DropdownContainer';
import {
    selectPriceToggleArrow,
    selectPriceToggleButton,
    selectPriceToggleLabel,
    selectPriceToggleText
} from './SelectPrice.styles.css';

const priceFrequencyOptions: { [key: PriceFrequency]: string } = {
    per_month: 'pcm',
    per_week: 'pcw'
};

const priceOptions = [
    '25',
    '50',
    '75',
    '100',
    '125',
    '150',
    '175',
    '200',
    '225',
    '250',
    '275',
    '300',
    '325',
    '350',
    '375',
    '400',
    '425',
    '450',
    '475',
    '500',
    '550',
    '600',
    '650',
    '700',
    '750',
    '800',
    '850',
    '900',
    '950',
    '1000',
    '1250',
    '1500',
    '1750',
    '2000',
    '2500',
    '3000',
    '3500',
    '4000',
    '4500',
    '5000',
    '7500',
    '8000',
    '8500',
    '9000',
    '9500',
    '10000',
    '12500',
    '15000',
    '17500',
    '20000',
    '25000'
];

type prices = typeof priceOptions[number];

export type PriceFrequency = '' | 'per_week' | 'per_month' | string;
export type PriceMin = prices;
export type PriceMax = prices;

type SelectPriceToRentProps = {
    value: {
        priceMin: PriceMin;
        priceMax: PriceMax;
        priceFrequency: PriceFrequency;
    };
    onChange: ({
        priceMin,
        priceMax,
        priceFrequency
    }: {
        priceMin: PriceMin;
        priceMax: PriceMax;
        priceFrequency: PriceFrequency;
    }) => void;
};

export const SelectPriceToRent = ({
    value,
    onChange
}: SelectPriceToRentProps) => {
    const [isActive, setIsActive] = useState(false);
    const rootNode = useRef() as MutableRefObject<HTMLDivElement>;

    const toggle = () => {
        setIsActive(!isActive);
    };

    const priceButtonTextToRent = buildSelectPriceTitleToRent(
        value.priceMin,
        value.priceMax,
        value.priceMin,
        value.priceMax,
        value.priceFrequency
    );

    const priceMinOptions =
        value.priceMax !== ''
            ? priceOptions.slice(0, priceOptions.indexOf(value.priceMax) + 1)
            : priceOptions;

    const priceMaxOptions =
        value.priceMin !== ''
            ? priceOptions.slice(priceOptions.indexOf(value.priceMin))
            : priceOptions;

    const priceFrequency = priceFrequencyOptions[value.priceFrequency];

    const frequencyOptions = [
        { id: 'per_month', text: 'Month' },
        { id: 'per_week', text: 'Week' }
    ];

    const minPriceOptions = priceMinOptions.map((priceMinOption, index) => {
        const price = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
        }).format(Number(priceMinOption));

        return {
            id: priceMinOption,
            text: `${price} ${priceFrequency}`
        };
    });

    const maxPriceOptions = priceMaxOptions.map((priceMaxOption, index) => {
        const price = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
        }).format(Number(priceMaxOption));

        return {
            id: priceMaxOption,
            text: `${price} ${priceFrequency}`
        };
    });

    const minPriceDefaultOption = {
        id: '',
        text: 'No min'
    };

    const maxPriceDefaultOption = {
        id: '',
        text: 'No max'
    };

    return (
        <div data-testid="any_price" ref={rootNode}>
            <button
                className={`${selectPriceToggleButton} ${fontGroups.ladle}`}
                onClick={toggle}
            >
                <label className={selectPriceToggleLabel}>Price range</label>
                <span className={selectPriceToggleText}>
                    {priceButtonTextToRent}
                </span>
                <div className={selectPriceToggleArrow} aria-hidden>
                    {isActive ? <ChevronUpMedium /> : <ChevronDownMedium />}
                </div>
            </button>
            <DropdownContainer isActive={isActive} isAlignedRight>
                <Select
                    options={frequencyOptions}
                    onSelectedOptionChange={(o) => {
                        onChange({
                            ...value,
                            priceFrequency: o.id
                        });
                    }}
                    selectedOption={
                        frequencyOptions.find(
                            (o) => o.id === value.priceFrequency
                        ) ?? frequencyOptions[0]
                    }
                    label="Price per"
                    data-testid="desktop_price_frequency-filter-select"
                />

                <Select
                    options={[minPriceDefaultOption, ...minPriceOptions]}
                    onSelectedOptionChange={(o) => {
                        onChange({
                            ...value,
                            priceMin: o.id
                        });
                    }}
                    selectedOption={
                        minPriceOptions.find((o) => o.id === value.priceMin) ??
                        minPriceDefaultOption
                    }
                    label="Minimum price"
                    data-testid="min_price"
                />

                <Select
                    options={[maxPriceDefaultOption, ...maxPriceOptions]}
                    onSelectedOptionChange={(o) => {
                        onChange({
                            ...value,
                            priceMax: o.id
                        });
                    }}
                    selectedOption={
                        maxPriceOptions.find((o) => o.id === value.priceMax) ??
                        maxPriceDefaultOption
                    }
                    label="Maximum price"
                    data-testid="max_price"
                />
            </DropdownContainer>
        </div>
    );
};
